import React, { useEffect, useState, ReactElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import SegmentScript from "segment";
import TrackPageViews from "TrackPageViews";

import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import TagManager from "react-gtm-module";
import { Auth0Provider } from "@auth0/auth0-react";

import CustomSnackbar, { AlertType } from "components/common/Snackbar";
import AppInitializer from "components/common/AppInitializer";
import PageLoader from "components/common/PageLoader";
import {
  redirectToEnhancedPortal,
  isRunningAsEnhancedPortal,
} from "components/Screens/Login/SignIn/enhancedPortalHandoff";
import useScrollToTop from "hooks/useScrollToTop";

import { getUserAdditionalInfo } from "selectors/user";
import { RootState } from "store/types";

import Routes from "routes";

import { UserAdditionalInfo } from "store/actions/UserAdditionalInfo";
import { GetPlatformConfig } from "store/actions/PlatformConfig";

import { baseUrl } from "util/APIBaseUrl";
import { captureCampaignParams, getHandoffNextRoute } from "util/postLoginRedirect";
import { shouldHandOffToEnhancedPortal } from "util/enhancedPortalGate";

import "styles/Typography.scss";
import "styles/Colors.scss";
import "styles/BoxShadows.scss";
import "styles/BorderRadius.scss";
import "./App.css";
import "./styles.css";
import "./customFieldsStyling.scss";

// PRD-2165: bound on enhanced-portal handoff retries after a failed code mint.
const MAX_HANDOFF_ATTEMPTS = 3;

interface LoggedInUser {
  userId: string;
  customerId: string;
  type: "edge" | "customer" | "employee";
}

interface CallToAction {
  text?: string;
  onClick?: () => void;
}

interface AppContentProps {
  open: boolean;
  message: string;
  type: AlertType;
  snackbarMessageContent: ReactElement | null;
  prefixIcon: ReactElement | null;
  callToAction: CallToAction;
  environment?: string;
}

function App(): React.ReactElement {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigateTo = useNavigate();

  const {
    open,
    type,
    message = "",
    snackbarMessageContent = null,
    prefixIcon = null,
    callToAction = {},
  } = useSelector((state: RootState) => state?.Utility?.snackbar);

  const platformConfig = useSelector((state: RootState) => state.PlatformConfig.platformConfig);
  const { AUTH0_DOMAIN: domain, AUTH0_CLIENT_ID: clientId, AUTH0_AUDIENCE: audience } = platformConfig?.payload || {};

  useEffect(() => {
    const userFromStorage = JSON.parse(localStorage.getItem("loggedInUser") || "null") as LoggedInUser | null;
    const accessToken = localStorage.getItem("accessToken");
    // If user and accesstoken is not null- Send request to fetch additional user details
    if (accessToken && userFromStorage) {
      const reqUrl = {
        edge: `user/${userFromStorage.userId}/summary`,
        customer: `customer/${userFromStorage.customerId}/info`,
        employee: `employee/${userFromStorage.userId}`,
      };
      dispatch(UserAdditionalInfo(reqUrl[userFromStorage.type]));
    } else if (
      (!accessToken || !userFromStorage) &&
      (location.pathname === "/employee" || location.pathname === "/admin" || location.pathname === "/signup")
    ) {
      // Keep the query string and hash. /signup is a campaign landing page (PRD-3035) and
      // dropping them here would strip the utm_* / gclid params from the URL on load.
      navigateTo(`${location.pathname}${location.search}${location.hash}`);
    }
    // Mount-only on purpose: adding the deps would refetch user info on every navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (platformConfig?.payload?.GTM) {
      const { id, auth, preview, isValid } = JSON.parse(platformConfig?.payload?.GTM);
      const tagManagerArgs = {
        gtmId: id,
        auth,
        preview,
      };

      if (isValid) {
        TagManager.initialize(tagManagerArgs);
      }
    }
  }, [platformConfig]);

  const content = (
    <AppInitializer>
      <AppContent
        open={open}
        message={message}
        type={type}
        snackbarMessageContent={snackbarMessageContent}
        prefixIcon={prefixIcon}
        callToAction={callToAction}
        environment={platformConfig?.payload?.ENVIRONMENT}
      />
    </AppInitializer>
  );

  if (!domain || !clientId || !audience) {
    return content;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience,
      }}
    >
      {content}
    </Auth0Provider>
  );
}

// AppContent
function AppContent({ environment, ...props }: AppContentProps): React.ReactElement {
  const dispatch = useDispatch();
  const userAdditionalInfo = useSelector(getUserAdditionalInfo);
  const platformConfig = useSelector((state: RootState) => state.PlatformConfig.platformConfig);
  const handoffStarted = React.useRef(false);
  const handoffAttempts = React.useRef(0);
  const platformConfigRequested = React.useRef(false);
  const [isHandingOff, setIsHandingOff] = useState(false);
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser") || "null") as LoggedInUser | null;

  useScrollToTop({ scrollToTopOnMount: true });

  useEffect(() => {
    if (loggedInUser?.type === "customer" && userAdditionalInfo?.status === "churned") {
      localStorage.clear();
    }
  }, [userAdditionalInfo.status, loggedInUser]);

  // First-touch campaign attribution for this tab's landing URL. Must run before the handoff
  // effect below can hand the browser off to the enhanced portal, or a utm-bearing landing hit
  // would never get captured.
  useEffect(() => {
    captureCampaignParams(window.location.search);
  }, []);

  // PRD-2165: enhanced-portal handoff. Lives here rather than in the login screens because
  // those only cover the password-login entry, and even there the onboarding navigation can
  // unmount them before the redirect runs. App is the one component every authenticated entry
  // renders, so a flagged user is handed off from login, set-password and plain refresh alike.
  useEffect(() => {
    if (handoffStarted.current) return;
    // Every user type hands off, each gated by its own per-user flag: customers and talent
    // (PRD-2124) read it top-level, edge employees (PRD-2719) read it nested under `.user`
    // because /user/:id/summary returns an envelope rather than the user record itself.
    if (!shouldHandOffToEnhancedPortal(loggedInUser?.type, userAdditionalInfo)) return;

    const enhancedPortalUrl = platformConfig?.payload?.ENHANCED_PORTAL_URL;
    if (!enhancedPortalUrl) {
      // Only the login screens fetch platform config today; request it once for the other
      // entries. loggedInUser is re-parsed each render, so without the ref this would refire.
      if (!platformConfig?.payload && !platformConfigRequested.current) {
        platformConfigRequested.current = true;
        dispatch(GetPlatformConfig({}));
      }
      return;
    }
    if (isRunningAsEnhancedPortal(enhancedPortalUrl)) return;

    handoffStarted.current = true;
    handoffAttempts.current += 1;
    // Swap the route tree for a loader before handing off. redirectToEnhancedPortal clears
    // localStorage and then assigns window.location, which does not navigate synchronously —
    // and the auth gate in routes/index.js re-reads accessToken on every render, so any render
    // landing in that gap would flash the logged-out view before the browser leaves the page.
    setIsHandingOff(true);
    redirectToEnhancedPortal({ baseUrl, enhancedPortalUrl, next: getHandoffNextRoute() }).then(
      (redirected: boolean) => {
        // Minting the handoff code failed; it fails safe and keeps the legacy session. Release the
        // latch so a later render can retry, but cap the attempts: loggedInUser is re-parsed every
        // render, so an uncapped release would retry on each one and hammer /auth/handoff-code.
        if (redirected) return;
        setIsHandingOff(false);
        if (handoffAttempts.current < MAX_HANDOFF_ATTEMPTS) handoffStarted.current = false;
      }
    );
  }, [userAdditionalInfo, platformConfig, loggedInUser, dispatch]);

  // Handoff in flight: hold a loader rather than rendering routes the cleared session would
  // bounce to the login screen.
  if (isHandingOff) {
    return <PageLoader />;
  }

  return (
    <>
      <CustomSnackbar {...props} />
      <SegmentScript environment={environment} />
      <TrackPageViews />
      <Box
        sx={{
          display: "flex",
          backgroundColor: "#F7F7F7",
          height: "100vh",
        }}
      >
        <CssBaseline />
        <Routes />
      </Box>
    </>
  );
}

export default App;
